/* eslint-disable no-nested-ternary */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMediaQuery } from 'react-responsive';
import toast from 'react-hot-toast';
import { FaSistrix, FaCircleExclamation } from 'react-icons/fa6';
import { uniqBy } from 'lodash';
import {
  FieldVisibilityTarget,
  IpstackResponse,
  LocatorResponse,
  TranslationTarget,
} from '../lib/dto';
import { SearchFilters } from './SearchFilters';
import { Store } from './Store';
import { Map, MapFocus } from './Map';
import { api } from '../lib/api';
import { PoweredByLink } from './PoweredByLink';
import { Button } from './Button';

export interface LocatorProps {
  data: LocatorResponse;
}

export type StoreWithDistance = LocatorProps['data']['stores'][number] & {
  distance: number | null;
};

export type GroupedFieldVisibilities = {
  targets: Record<
    FieldVisibilityTarget,
    Array<LocatorResponse['fieldVisibilities'][number]>
  >;
  customFields: Record<
    string,
    Array<LocatorResponse['fieldVisibilities'][number]>
  >;
};

export const Locator: React.FC<LocatorProps> = ({
  data: { stores, settings, customFields, translations, fieldVisibilities },
}) => {
  const [state, setState] = useState<{
    userPosition: { lat: number; lng: number } | null;
    selectedSearchFilters: string[];
    selectedTab: 'list' | 'map';
    mapBounds: google.maps.LatLngBounds | null;
    mapFocus: MapFocus;
    /**
     * This field is used when in mobile view to save the selected store without
     * updating the actual mapFocus variable (which would trigger a bounds
     * change that would change the items in the list view).
     */
    mobileMapFocus: MapFocus;
  }>({
    userPosition: null,
    selectedSearchFilters: [],
    selectedTab: 'list',
    mapBounds: null,
    mapFocus: { type: 'point', lat: 47.114854, lng: -101.297808, zoom: 3 },
    mobileMapFocus: {
      type: 'point',
      lat: 47.114854,
      lng: -101.297808,
      zoom: 3,
    },
  });
  const isLargeDeviceMediaQuery = useMediaQuery({
    query: '(min-width: 768px)',
  });

  useEffect(() => {
    console.log(
      'Attempting to get position using navigator.geolocation.getCurrentPosition()',
    );
    navigator.geolocation.getCurrentPosition(
      (position) => {
        console.log(
          `Got position via navigator.geolocation.getCurrentPosition(): latitude=${position.coords.latitude}, longitude=${position.coords.longitude}`,
        );
        setState((prevState) => {
          return {
            ...prevState,
            userPosition: {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            },
            mapFocus: {
              type: 'point',
              lat: position.coords.latitude,
              lng: position.coords.longitude,
              zoom: settings.defaultMapZoom,
            },
            mobileMapFocus: {
              type: 'point',
              lat: position.coords.latitude,
              lng: position.coords.longitude,
              zoom: settings.defaultMapZoom,
            },
          };
        });
      },
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      async (error) => {
        // If navigator.geolocation.getCurrentPosition() fails we fallback to
        // ipstack data
        console.log(
          `Failed to get position via navigator.geolocation.getCurrentPosition(): ${
            error.code === error.PERMISSION_DENIED
              ? 'permission denied'
              : error.code === error.POSITION_UNAVAILABLE
              ? 'position unavailable'
              : error.code === error.TIMEOUT
              ? 'timeout'
              : 'not possible'
          }`,
        );
        let ipstackData: IpstackResponse;
        try {
          ipstackData = await api.ipstack();
        } catch (err) {
          console.log('Failed to get position via ipstack');
          toast.error('Failed to get initial location', {
            icon: <FaCircleExclamation />,
            position: 'bottom-center',
            style: {
              fontSize: '13px',
              color: '#D90D1E',
            },
          });
          return;
        }
        console.log(
          `Got position via ipstack: latitude=${ipstackData.ipstack.latitude}, longitude=${ipstackData.ipstack.longitude}`,
        );
        setState((prevState) => {
          return {
            ...prevState,
            userPosition: {
              lat: ipstackData.ipstack.latitude,
              lng: ipstackData.ipstack.longitude,
            },
            mapFocus: {
              type: 'point',
              lat: ipstackData.ipstack.latitude,
              lng: ipstackData.ipstack.longitude,
              zoom: settings.defaultMapZoom,
            },
            mobileMapFocus: {
              type: 'point',
              lat: ipstackData.ipstack.latitude,
              lng: ipstackData.ipstack.longitude,
              zoom: settings.defaultMapZoom,
            },
          };
        });
      },
    );
  }, [settings.defaultMapZoom]);

  const storesSortedByDistance: StoreWithDistance[] = useMemo(() => {
    if (state.userPosition) {
      const from = {
        lat: state.userPosition.lat,
        lng: state.userPosition.lng,
      };
      return stores
        .map((store) => {
          const to = { lat: store.lat, lng: store.lng };
          // The distance in meters
          const distance =
            google.maps.geometry.spherical.computeDistanceBetween(from, to);
          return { ...store, distance };
        })
        .sort((a, b) => {
          return a.distance - b.distance;
        });
    }
    return stores.map((store) => {
      return {
        ...store,
        distance: null,
      };
    });
  }, [state.userPosition, stores]);

  const storesFiltered = useMemo(() => {
    if (!state.mapBounds) {
      // We need the map to be initialized and the map bounds set before we can
      // filter the stores
      return [];
    }

    let memoValue = storesSortedByDistance.filter((store) => {
      return state.mapBounds?.contains({ lat: store.lat, lng: store.lng });
    });

    if (state.selectedSearchFilters.length > 0) {
      memoValue = memoValue.filter((store) => {
        return state.selectedSearchFilters.every((id) => {
          return !!store.searchFilters.find((f) => f.id === id);
        });
      });
    }

    return memoValue;
  }, [state.mapBounds, state.selectedSearchFilters, storesSortedByDistance]);

  const searchFilters = useMemo(() => {
    return uniqBy(
      storesFiltered
        .map((store) => {
          return store.searchFilters;
        })
        .flat(1),
      (searchFilter) => {
        return searchFilter.id;
      },
    );
  }, [storesFiltered]);

  const storeListRef = useRef<HTMLDivElement | null>(null);
  const storeRefs = useRef<Record<string, HTMLDivElement>>({});

  const onMarkerClickCallback = useCallback(
    (store: StoreWithDistance) => {
      setState((prevState) => {
        return {
          ...prevState,
          mapFocus: { type: 'store', id: store.id },
        };
      });

      // Wait 500ms for the map to finish updating (panning to the new focus) and
      // then scroll the store into view. This is only used for large devices
      // where the tabs aren't shown.
      setTimeout(() => {
        if (!isLargeDeviceMediaQuery) {
          // Don't do anything on small devices
          return;
        }

        const el = storeRefs.current[store.id];

        if (!el || !storeListRef.current) {
          return;
        }

        // I dunno why offsetTop works but it works
        const diff = el.offsetTop - storeListRef.current.offsetTop;
        storeListRef.current.scrollTo({ top: diff });

        // el.scrollIntoView({
        //   block: 'nearest',
        //   inline: 'start',
        //   behavior: 'smooth',
        // });
      }, 500);
    },
    [isLargeDeviceMediaQuery],
  );

  const onBoundsChangedCallback = useCallback(
    (bounds: google.maps.LatLngBounds) => {
      setState((prevState) => {
        return {
          ...prevState,
          mapBounds: bounds,
        };
      });
    },
    [],
  );

  const placesService = useRef<google.maps.places.PlacesService | null>(null);
  const searchInput = useRef<HTMLInputElement | null>(null);
  const autocomplete = useRef<google.maps.places.Autocomplete | null>(null);
  const onSearchCallback = useCallback(() => {
    const search = searchInput.current?.value;

    if (!search || !placesService.current) {
      return;
    }

    placesService.current.findPlaceFromQuery(
      {
        // Select the geometry fields so we can get the latitude and longitude
        // See for full list of fields: https://developers.google.com/maps/documentation/javascript/places#place_search_fields
        fields: ['geometry'],
        query: search,
      },
      (result, status) => {
        if (
          status !== google.maps.places.PlacesServiceStatus.OK &&
          status !== google.maps.places.PlacesServiceStatus.ZERO_RESULTS &&
          status !== google.maps.places.PlacesServiceStatus.NOT_FOUND
        ) {
          toast.error('Failed to look up address', {
            icon: <FaCircleExclamation />,
            position: 'bottom-center',
            style: {
              fontSize: '13px',
              color: '#D90D1E',
            },
          });
          return;
        }
        if (!result || result.length === 0) {
          toast.error('Address not found', {
            icon: <FaCircleExclamation />,
            position: 'bottom-center',
            style: {
              fontSize: '13px',
              color: '#D90D1E',
            },
          });
          return;
        }
        const place = result[0];

        const lat = place.geometry?.location?.lat();
        const lng = place.geometry?.location?.lng();

        console.log('place', place, lat, lng);

        if (lat && lng) {
          setState((prevState) => {
            return {
              ...prevState,
              userPosition: {
                lat,
                lng,
              },
              mapFocus: {
                type: 'point',
                lat,
                lng,
                zoom: settings.defaultMapZoom,
              },
              mobileMapFocus: {
                type: 'point',
                lat,
                lng,
                zoom: settings.defaultMapZoom,
              },
            };
          });
        }
      },
    );
  }, [settings.defaultMapZoom]);

  const groupedTranslations = useMemo(() => {
    const targetToTranslation: Partial<
      Record<TranslationTarget, (typeof translations)[number]>
    > = {};
    const customFieldIdToTranslation: Record<
      string,
      (typeof translations)[number]
    > = {};
    const searchFilterIdToTranslation: Record<
      string,
      (typeof translations)[number]
    > = {};

    translations.forEach((t) => {
      if (t.target) {
        targetToTranslation[t.target] = t;
      } else if (t.customFieldId) {
        customFieldIdToTranslation[t.customFieldId] = t;
      } else if (t.searchFilterId) {
        searchFilterIdToTranslation[t.searchFilterId] = t;
      }
    });

    return {
      targets: targetToTranslation,
      customFields: customFieldIdToTranslation,
      searchFilters: searchFilterIdToTranslation,
    };
  }, [translations]);

  const groupedFieldVisibilities: GroupedFieldVisibilities = useMemo(() => {
    const targetToFieldVisibility: Record<
      FieldVisibilityTarget,
      Array<(typeof fieldVisibilities)[number]>
    > = {
      email: [],
      notes: [],
      phone: [],
      searchFilters: [],
      website: [],
    };
    const customFieldIdToFieldVisibility: Record<
      string,
      Array<(typeof fieldVisibilities)[number]>
    > = {};

    fieldVisibilities.forEach((fieldVisibility) => {
      if (fieldVisibility.target) {
        targetToFieldVisibility[fieldVisibility.target].push(fieldVisibility);
      } else if (fieldVisibility.customFieldId) {
        if (!customFieldIdToFieldVisibility[fieldVisibility.customFieldId]) {
          customFieldIdToFieldVisibility[fieldVisibility.customFieldId] = [];
        }
        customFieldIdToFieldVisibility[fieldVisibility.customFieldId].push(
          fieldVisibility,
        );
      }
    });

    return {
      targets: targetToFieldVisibility,
      customFields: customFieldIdToFieldVisibility,
    };
  }, [fieldVisibilities]);

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '15px',
        fontSize: '13px',
      }}
    >
      {/* This div is needed to initialize the Google Maps Places Service */}
      <div
        ref={(ref) => {
          if (ref && !placesService.current) {
            placesService.current = new google.maps.places.PlacesService(ref);
          }
        }}
        style={{ display: 'none' }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
        <div style={{ display: 'flex', gap: '10px' }}>
          <input
            placeholder={
              groupedTranslations.targets.searchPlaceholder?.value ||
              'Enter a location'
            }
            ref={(ref) => {
              if (ref) {
                searchInput.current = ref;

                // Autocomplete component has already been initialized
                if (autocomplete.current) {
                  return;
                }

                // Initialize a new autocomplete component
                autocomplete.current = new google.maps.places.Autocomplete(ref);
                // Select the geometry field so we can get the latitute and longitude
                // See for full list of fields: https://developers.google.com/maps/documentation/javascript/places#place_search_fields
                autocomplete.current.setFields(['geometry']);
                autocomplete.current.addListener('place_changed', () => {
                  const place = autocomplete.current?.getPlace();

                  if (!place) {
                    return;
                  }

                  const lat = place.geometry?.location?.lat();
                  const lng = place.geometry?.location?.lng();

                  if (typeof lat === 'number' && typeof lng === 'number') {
                    setState((prevState) => {
                      return {
                        ...prevState,
                        userPosition: {
                          lat,
                          lng,
                        },
                        mapFocus: {
                          type: 'point',
                          lat,
                          lng,
                          zoom: settings.defaultMapZoom,
                        },
                        mobileMapFocus: {
                          type: 'point',
                          lat,
                          lng,
                          zoom: settings.defaultMapZoom,
                        },
                      };
                    });
                  }
                });
              }
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                onSearchCallback();
              }
            }}
            style={{
              outline: 'none',
              border: '1px solid #ccc',
              boxShadow: 'none',
              padding: '10px',
              borderRadius: '3px',
              width: '100%',
            }}
            className="simple-store-locator-search"
          />
          <Button
            themeColor={settings.themeColor}
            onClick={onSearchCallback}
            style={{ flexBasis: '40px', flexGrow: 0 }}
          >
            <FaSistrix />
          </Button>
        </div>
        {searchFilters.length > 0 && (
          <div>
            <SearchFilters
              searchFilters={searchFilters}
              selected={state.selectedSearchFilters}
              translations={groupedTranslations.searchFilters}
              themeColor={settings.themeColor}
              onChange={(selected) => {
                setState((prevState) => {
                  return {
                    ...prevState,
                    selectedSearchFilters: selected,
                  };
                });
                if (storeListRef.current) {
                  storeListRef.current.scrollTo({ top: 0 });
                }
              }}
            />
          </div>
        )}
      </div>
      {isLargeDeviceMediaQuery && (
        <div
          style={{
            display: 'flex',
            gap: '15px',
          }}
        >
          <div
            style={{
              flexBasis: '33%',
            }}
          >
            <div
              ref={storeListRef}
              style={{
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'auto',
                height: '580px',
              }}
            >
              {storesFiltered.length === 0 && (
                <p
                  style={{
                    textAlign: 'center',
                    padding: 0,
                    margin: 0,
                    fontSize: '13px',
                  }}
                >
                  {groupedTranslations.targets.noLocationsFound?.value ||
                    'No locations found'}
                </p>
              )}
              {storesFiltered.length > 0 &&
                (settings.limitStorefrontList > 0
                  ? storesFiltered.slice(0, settings.limitStorefrontList)
                  : storesFiltered
                ).map((store, index) => {
                  return (
                    <Store
                      ref={(ref) => {
                        if (ref) {
                          storeRefs.current[store.id] = ref;
                        }
                      }}
                      key={store.id}
                      store={store}
                      customFields={customFields}
                      selected={
                        state.mapFocus.type === 'store' &&
                        state.mapFocus.id === store.id
                      }
                      themeColor={settings.themeColor}
                      distanceUnit={settings.distanceUnit}
                      translations={{
                        targets: groupedTranslations.targets,
                        customFields: groupedTranslations.customFields,
                      }}
                      borderBottom={index !== storesFiltered.length - 1}
                      showDetailsLabels={settings.showDetailsLabels}
                      groupedFieldVisibilities={groupedFieldVisibilities}
                      onClick={() => {
                        setState((prevState) => {
                          return {
                            ...prevState,
                            mapFocus: { type: 'store', id: store.id },
                          };
                        });
                      }}
                    />
                  );
                })}
            </div>
            <PoweredByLink />
          </div>
          <div
            style={{
              flexBasis: '66%',
              height: '600px',
            }}
          >
            <Map
              stores={storesFiltered}
              focus={state.mapFocus}
              markerColor={settings.markerColor}
              themeColor={settings.themeColor}
              translations={groupedTranslations.targets}
              groupedFieldVisibilities={groupedFieldVisibilities}
              showDetailsLabels={settings.showDetailsLabels}
              customFields={customFields}
              onMarkerClick={onMarkerClickCallback}
              onBoundsChanged={onBoundsChangedCallback}
            />
          </div>
        </div>
      )}
      {!isLargeDeviceMediaQuery && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '15px',
          }}
        >
          <div style={{ display: 'flex' }}>
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <div
              onClick={() => {
                setState((prevState) => {
                  return {
                    ...prevState,
                    selectedTab: 'list',
                    mobileMapFocus: prevState.mapFocus,
                  };
                });
                // After clicking on the list tab, if the map focus is a store,
                // wait a little and then scroll the store into view.
                setTimeout(() => {
                  if (state.mapFocus.type === 'store' && storeListRef.current) {
                    const el = storeRefs.current[state.mapFocus.id];
                    if (!el) {
                      return;
                    }
                    const diff = el.offsetTop - storeListRef.current.offsetTop;
                    storeListRef.current.scrollTo({ top: diff });
                  }
                }, 500);
              }}
              style={{
                flexBasis: '50%',
                padding: '10px',
                textAlign: 'center',
                cursor: 'pointer',
                color: state.selectedTab === 'list' ? '#000000' : '#7f8386',
                boxShadow:
                  state.selectedTab === 'list'
                    ? `0px 4px 0px 0px ${settings.themeColor}`
                    : '0px 4px 0px 0px #e1e3e5',
              }}
              className={`simple-store-locator-tab ${
                state.selectedTab === 'list'
                  ? 'simple-store-locator-tab-active'
                  : 'simple-store-locator-tab-inactive'
              }`}
            >
              List
            </div>
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <div
              onClick={() => {
                setState((prevState) => {
                  return {
                    ...prevState,
                    selectedTab: 'map',
                    mapFocus: prevState.mobileMapFocus,
                  };
                });
              }}
              style={{
                flexBasis: '50%',
                padding: '10px',
                textAlign: 'center',
                cursor: 'pointer',
                color: state.selectedTab === 'map' ? '#000000' : '#7f8386',
                boxShadow:
                  state.selectedTab === 'map'
                    ? `0px 4px 0px 0px ${settings.themeColor}`
                    : '0px 4px 0px 0px #e1e3e5',
              }}
              className={`simple-store-locator-tab ${
                state.selectedTab === 'map'
                  ? 'simple-store-locator-tab-active'
                  : 'simple-store-locator-tab-inactive'
              }`}
            >
              Map
            </div>
          </div>
          <div
            style={{
              display: state.selectedTab === 'list' ? 'block' : 'none',
            }}
          >
            <div
              ref={storeListRef}
              style={{ height: '580px', overflowY: 'auto' }}
            >
              {storesFiltered.length === 0 && (
                <p
                  style={{
                    textAlign: 'center',
                    padding: 0,
                    margin: 0,
                    fontSize: '13px',
                  }}
                >
                  {groupedTranslations.targets.noLocationsFound?.value ||
                    'No locations found'}
                </p>
              )}
              {storesFiltered.length > 0 &&
                (settings.limitStorefrontList > 0
                  ? storesFiltered.slice(0, settings.limitStorefrontList)
                  : storesFiltered
                ).map((store, index) => {
                  return (
                    <Store
                      ref={(ref) => {
                        if (ref) {
                          storeRefs.current[store.id] = ref;
                        }
                      }}
                      key={store.id}
                      store={store}
                      customFields={customFields}
                      selected={
                        state.mobileMapFocus?.type === 'store' &&
                        state.mobileMapFocus?.id === store.id
                      }
                      themeColor={settings.themeColor}
                      distanceUnit={settings.distanceUnit}
                      translations={{
                        targets: groupedTranslations.targets,
                        customFields: groupedTranslations.customFields,
                      }}
                      borderBottom={index !== storesFiltered.length - 1}
                      showDetailsLabels={settings.showDetailsLabels}
                      groupedFieldVisibilities={groupedFieldVisibilities}
                      onClick={() => {
                        setState((prevState) => {
                          return {
                            ...prevState,
                            mobileMapFocus: { type: 'store', id: store.id },
                          };
                        });
                      }}
                    />
                  );
                })}
            </div>
            <PoweredByLink />
          </div>
          <div
            style={{
              height: state.selectedTab === 'map' ? '300px' : 0,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                height: '300px',
                width: '100%',
              }}
            >
              <Map
                stores={storesFiltered}
                focus={state.mapFocus}
                markerColor={settings.markerColor}
                themeColor={settings.themeColor}
                translations={groupedTranslations.targets}
                groupedFieldVisibilities={groupedFieldVisibilities}
                showDetailsLabels={settings.showDetailsLabels}
                customFields={customFields}
                onMarkerClick={onMarkerClickCallback}
                onBoundsChanged={onBoundsChangedCallback}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
